import { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Breadcrumbs, Link, useTheme } from '@mui/material';
import { IRoute } from '@interfaces/App.interface';
import styled from '@emotion/styled';
import { useAppSelector } from '@redux/store';
import { Icon } from '@ts/storybook';
import { useRouterContext } from '@utils/context/RouterContext';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Grid, Typography } from '@ts/storybook';
import { useTranslation } from 'react-i18next';

interface BreadcrumbsProps {}

const useStyles = makeStyles<Theme, { theme: Theme }>(() => ({
  gridBreadCrumb: props => ({
    width: '100%',
    position: 'sticky',
    top: '0px',
    padding: '10px',
    paddingBottom: '0px',
    height: '52px',
    zIndex: '10',
    backgroundColor: 'white',
    overflowX: 'auto',
    overflowY: 'hidden',
    display: 'flex',
    // Media query sm
    [props.theme.breakpoints.down('sm')]: {
      height: '50px',
    },
  }),
}));

const StyledBreadcrumbs =
  Breadcrumbs &&
  styled(Breadcrumbs)`
    .MuiBreadcrumbs-ol {
      width: max-content;
    }
  `;

const TBreadcrumbs = (props: BreadcrumbsProps) => {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const { navigate } = useRouterContext();
  const historyRoutes = useAppSelector(state => state.router.historyRoutes);
  const [historyRoutesLocal, setHistoryRoutesLocal] = useState<IRoute[]>([]);

  //#region Handlers
  const onClickBreadcrumb = (route: IRoute) => {
    navigate(route.fullPath || route.matchPath, { state: { title: route.pageTitle } });
  };

  const onClickLastBread = () => {
    const route = historyRoutes[historyRoutes.length - 1];
    if (route.routeLastBread) {
      navigate(route.routeLastBread, { state: { title: route.pageTitle } });
    }
  };
  //#endregion

  //#region Function
  const isLastStepLinked = () => {
    return historyRoutes[historyRoutes.length - 1]?.routeLastBread;
  };

  const generateRouteName = (route: IRoute) => {
    if (route.pageTitle) return t(route.pageTitle);
    return '';
  };

  const removeRepetitiveRoutes = (arr: IRoute[]) => {
    return arr.reduce((acc: IRoute[], current: IRoute) => {
      if (!acc.find(item => item.pageTitle === current.pageTitle)) {
        acc.push(current);
      }
      return acc;
    }, []);
  };
  //#endregion

  //#region Render
  const renderStepBreadcrumb = (item: IRoute, index: number) => {
    return (
      <Link
        underline="hover"
        color="inherit"
        key={(item.fullPath || item.matchPath) + index}
        style={{ cursor: 'pointer' }}
        onClick={() => onClickBreadcrumb(item)}
      >
        {generateRouteName(item)}
      </Link>
    );
  };

  const renderLastStepBreadcrumb = (item: IRoute, index: number) => {
    const content = (
      <Typography
        variant="h6"
        key={(item.fullPath || item.matchPath) + index}
        color="text.primary"
        text={generateRouteName(item)}
      />
    );
    if (!item.routeLastBread) return content;
    return (
      <Link
        underline="hover"
        color="inherit"
        key={(item.fullPath || item.matchPath) + index}
        style={{ cursor: 'pointer' }}
        onClick={() => onClickLastBread()}
      >
        {content}
      </Link>
    );
  };
  //#endregion

  useEffect(() => {
    setHistoryRoutesLocal(removeRepetitiveRoutes(historyRoutes));
  }, [historyRoutes.length]);

  return (
    <Grid id={'grid-breadcrumbs'} className={classes.gridBreadCrumb}>
      <StyledBreadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
        {historyRoutesLocal.map((item, index) => {
          if (index !== historyRoutesLocal.length - 1) {
            return renderStepBreadcrumb(item, index);
          }
          return renderLastStepBreadcrumb(item, index);
        })}
      </StyledBreadcrumbs>
      {isLastStepLinked() && <Icon sx={{ marginLeft: '5px' }} icon={'settings'} />}
    </Grid>
  );
};

export default TBreadcrumbs;
